import dayjs from 'dayjs';
import ReactECharts from 'echarts-for-react';
import { Activity, CalendarRange, ChevronLeft, ChevronRight, ExternalLink, Eye, EyeOff } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getMarketEventsInRange, marketEventCategories } from '../../data/marketEvents';
import { analyzeEventMovement, buildEventSeries, eventRangeLabel } from './eventAnnotations';

function signed(value, digits = 2) {
  if (!Number.isFinite(value)) return '—';
  return `${value > 0 ? '+' : ''}${value.toFixed(digits)}`;
}

function suggestedEvent(events, lastDate) {
  return [...events].reverse().find(event => {
    if (event.ongoing) return true;
    const analysisEnd = event.end || dayjs(event.start).add(7, 'day').format('YYYY-MM-DD');
    return analysisEnd <= lastDate;
  }) || events.at(-1);
}

export default function AnnotatedTimeChart({
  option,
  dates = [],
  height = 390,
  fuels = ['BENZINA', 'GASOLIO'],
  analysisSeries = [],
  analysisUnit = 'price',
}) {
  const [showEvents, setShowEvents] = useState(false);
  const [compact, setCompact] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches);
  const events = useMemo(() => getMarketEventsInRange(dates, fuels), [dates, fuels]);
  const [activeId, setActiveId] = useState(null);
  const [railState, setRailState] = useState({ canGoBack: false, canGoForward: false });
  const eventRailRef = useRef(null);

  useEffect(() => {
    const query = window.matchMedia('(max-width: 639px)');
    const update = event => setCompact(event.matches);
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!events.length) {
      setActiveId(null);
      return;
    }
    if (!events.some(event => event.id === activeId)) setActiveId(suggestedEvent(events, dates.at(-1))?.id || null);
  }, [events, dates, activeId]);

  useEffect(() => {
    const rail = eventRailRef.current;
    const activeButton = rail?.querySelector(`[data-event-id="${activeId}"]`);
    if (!showEvents || !rail || !activeButton) return;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const targetLeft = activeButton.offsetLeft - (rail.clientWidth - activeButton.clientWidth) / 2;
    rail.scrollTo({
      left: Math.max(0, targetLeft),
      behavior: reducedMotion ? 'auto' : 'smooth',
    });
  }, [activeId, showEvents]);

  useEffect(() => {
    const rail = eventRailRef.current;
    if (!rail || !showEvents) return undefined;
    const update = () => setRailState({
      canGoBack: rail.scrollLeft > 2,
      canGoForward: rail.scrollLeft + rail.clientWidth < rail.scrollWidth - 2,
    });
    update();
    rail.addEventListener('scroll', update, { passive: true });
    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    resizeObserver?.observe(rail);
    window.addEventListener('resize', update);
    return () => {
      rail.removeEventListener('scroll', update);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [events.length, showEvents]);

  const activeEvent = events.find(event => event.id === activeId) || events.at(-1);
  const activeIndex = activeEvent ? events.findIndex(event => event.id === activeEvent.id) : -1;
  const movement = useMemo(
    () => activeEvent ? analyzeEventMovement(activeEvent, dates, analysisSeries) : null,
    [activeEvent, dates, analysisSeries]
  );
  const chartOption = useMemo(() => {
    const eventSeries = showEvents ? buildEventSeries(events, dates, compact, activeId) : null;
    return {
      ...option,
      tooltip: {
        ...option.tooltip,
        triggerOn: 'mousemove|click',
        enterable: true,
        confine: true,
      },
      legend: option.legend ? {
        ...option.legend,
        data: (option.series || []).filter(series => series.name && series.tooltip?.show !== false).map(series => series.name),
      } : undefined,
      series: [...(option.series || []), ...(eventSeries ? [eventSeries] : [])],
    };
  }, [option, events, dates, showEvents, compact, activeId]);

  if (!events.length) return <ReactECharts style={{ height }} option={option} />;

  const category = activeEvent ? marketEventCategories[activeEvent.category] : null;
  const delta = movement ? analysisUnit === 'price' ? movement.delta * 100 : movement.delta : null;
  const deltaSuffix = analysisUnit === 'price' ? ' c€/l' : ' punti indice';

  const onChartClick = params => {
    const eventId = params?.data?.eventId || params?.data?.[0]?.eventId;
    if (eventId) setActiveId(eventId);
  };

  const scrollEventRail = direction => {
    const rail = eventRailRef.current;
    if (!rail) return;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    rail.scrollBy({
      left: direction * Math.max(320, rail.clientWidth * .72),
      behavior: reducedMotion ? 'auto' : 'smooth',
    });
  };

  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-center justify-between gap-3 px-2 pb-2 pt-2 sm:px-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[10px] font-medium text-md-on-surface-variant">
          {Object.entries(marketEventCategories).map(([key, item]) => events.some(event => event.category === key) && (
            <span key={key} className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ backgroundColor: item.color }} />{item.label}</span>
          ))}
        </div>
        <button
          type="button"
          aria-pressed={showEvents}
          onClick={() => setShowEvents(value => !value)}
          className="md-press inline-flex min-h-9 items-center gap-2 rounded-full bg-md-surface-container px-3 text-[11px] font-bold text-md-on-surface hover:bg-md-surface-container-high focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-md-primary"
        >
          {showEvents ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
          {events.length} eventi
        </button>
      </div>

      <ReactECharts
        style={{ height, width: '100%', minWidth: 0 }}
        option={chartOption}
        notMerge
        lazyUpdate
        onEvents={{ click: onChartClick }}
      />

      {showEvents && (
        <div className="border-t border-md-outline-variant/70 px-2 pb-2 pt-3 sm:px-3">
          <div className="mb-2 hidden items-center justify-between gap-3 sm:flex">
            <span className="text-[10px] font-bold uppercase tracking-[.08em] text-md-on-surface-variant">Eventi nel periodo</span>
            <div className="flex items-center gap-1" aria-label="Scorri gli eventi">
              <button
                type="button"
                aria-label="Eventi precedenti"
                disabled={!railState.canGoBack}
                onClick={() => scrollEventRail(-1)}
                className="md-press inline-flex h-9 w-9 items-center justify-center rounded-full bg-md-surface-container text-md-on-surface transition-colors hover:bg-md-surface-container-high focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-md-primary disabled:cursor-default disabled:opacity-30"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                aria-label="Eventi successivi"
                disabled={!railState.canGoForward}
                onClick={() => scrollEventRail(1)}
                className="md-press inline-flex h-9 w-9 items-center justify-center rounded-full bg-md-surface-container text-md-on-surface transition-colors hover:bg-md-surface-container-high focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-md-primary disabled:cursor-default disabled:opacity-30"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div ref={eventRailRef} className="no-scrollbar flex max-w-full gap-2 overflow-x-auto pb-2" role="list" aria-label="Eventi nel periodo del grafico">
            {events.map((event, index) => {
              const eventCategory = marketEventCategories[event.category];
              const active = event.id === activeEvent?.id;
              return (
                <button
                  key={event.id}
                  type="button"
                  role="listitem"
                  data-event-id={event.id}
                  aria-current={active ? 'true' : undefined}
                  onClick={() => setActiveId(event.id)}
                  className={`md-press min-h-14 w-[190px] shrink-0 rounded-[18px_18px_6px_18px] px-3 py-2 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-md-primary ${active ? 'bg-md-primary-container text-md-on-primary-container ring-1 ring-inset ring-md-primary' : 'bg-md-surface-container-low text-md-on-surface hover:bg-md-surface-container'}`}
                >
                  <span className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[.08em]" style={{ color: eventCategory.color }}>
                    <span>E{index + 1}</span><span className="h-1 w-1 rounded-full" style={{ backgroundColor: eventCategory.color }} />{dayjs(event.start).format('DD/MM/YY')}
                  </span>
                  <span className="mt-1 block truncate text-xs font-bold">{event.shortTitle}</span>
                </button>
              );
            })}
          </div>

          {activeEvent && (
            <article className="mt-1 grid gap-4 rounded-[24px_24px_8px_24px] bg-md-surface-container-low p-4 sm:grid-cols-[minmax(0,1fr)_280px] sm:p-5">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[.08em]" style={{ color: category.color, backgroundColor: category.softColor }}>{`E${activeIndex + 1}`} · {category.label}</span>
                  {activeEvent.ongoing && <span className="rounded-full bg-md-primary px-2.5 py-1 text-[10px] font-bold uppercase tracking-[.08em] text-md-on-primary">In corso</span>}
                </div>
                <h3 className="mt-3 text-base font-bold leading-5 text-md-on-surface">{activeEvent.title}</h3>
                <div className="mt-2 flex items-start gap-2 text-xs font-medium text-md-on-surface-variant"><CalendarRange className="mt-0.5 h-3.5 w-3.5 shrink-0" />{eventRangeLabel(activeEvent)}</div>
                <p className="mt-3 text-xs leading-5 text-md-on-surface-variant">{activeEvent.note}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {activeEvent.facts.map(fact => <span key={fact} className="max-w-full break-words rounded-full bg-md-surface px-3 py-1.5 text-[10px] font-bold text-md-on-surface">{fact}</span>)}
                </div>
                <a href={activeEvent.source.url} target="_blank" rel="noreferrer" className="md-press mt-4 inline-flex min-h-10 max-w-full items-center gap-2 break-words rounded-full bg-md-primary px-4 text-xs font-bold text-md-on-primary hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-md-primary">
                  Fonte: {activeEvent.source.label}<ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>

              <div className="self-start rounded-[22px_8px_22px_22px] bg-md-primary-container px-4 py-4 text-md-on-primary-container">
                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[.08em]"><Activity className="h-3.5 w-3.5" />Variazione nel grafico</div>
                {movement ? (
                  <>
                    <div className="numbers mt-2 text-xl font-bold">{signed(delta)}{deltaSuffix} <span className="text-xs font-medium">({signed(movement.percent)}%)</span></div>
                    <div className="mt-1 text-[10px] font-medium leading-4">{movement.windowLabel} · {dayjs(movement.from).format('DD/MM')} → {dayjs(movement.to).format('DD/MM')}</div>
                    {movement.seriesCount > 1 && <div className="mt-1 text-[10px] leading-4">Valore centrale di {movement.seriesCount} serie; intervallo {signed(movement.minPercent)}% / {signed(movement.maxPercent)}%.</div>}
                  </>
                ) : <div className="mt-2 text-xs leading-5">Non ci sono abbastanza dati per calcolare la variazione.</div>}
                <p className="mt-3 border-t border-md-outline-variant pt-3 text-[10px] leading-4">
                  Calcolo sui prezzi MIMIT mostrati: confronto tra il dato disponibile all’inizio e quello alla fine del periodo. È una variazione temporale, non una prova di causalità.
                </p>
              </div>
            </article>
          )}
        </div>
      )}
    </div>
  );
}
